import {
  setAccountsForSTOperation,
  setCampaignsWithoutScopeData,
  setNegativesInListAndInAdGroupData,
  setNegativesInTheCampaignData,
  setScopesOfSTOperation,
} from "../../../features/searchTermOperationSlice";
import { setDisplayErrorSnackBar } from "../../../features/userSlice";
import { stOperationService } from "../../../services/api/search-term-operation.endpoints";
import store from "../../../store";

// get accounts
async function getAccounts(
  oauthTokenHeader: string,
  queryId: string,
  site_shared_uid: string,
  account_traffic_source_shared_uid: string | any,
  account_isactive: number | any
) {
  try {
    const response = await stOperationService.getAccountsBasedOnSite(
      oauthTokenHeader,
      queryId,
      site_shared_uid,
      account_traffic_source_shared_uid,
      account_isactive
    );
    if (!response || !response.status || response.status !== 200) {
      throw new Error("Failed to access accounts.");
    } else {
      const accounts = response?.data?.data?.query_output ?? [];
      // Allowed traffic sources
      const ALLOWED_SOURCES = new Set(["g_ads_search", "bing_ads_search"]);
      //filtered accounts from allowed traffic sources
      const filteredAccounts = accounts.filter((account: any) =>
        ALLOWED_SOURCES.has(account?.account_traffic_source)
      );
      store.dispatch(setAccountsForSTOperation(filteredAccounts)); // Set the fetched data
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      })
    );
  }
}

// get scopes based on website and account
async function getScopes(
  oauthTokenHeader: string,
  website_shared_uid: string,
  account_shared_uid: string
) {
  try {
    const response = await stOperationService.getAllScopes(
      oauthTokenHeader,
      website_shared_uid,
      account_shared_uid
    );
    if (!response || !response.status) {
      throw new Error("Failed to access scopes.");
    } else if (response.status === 200) {
      const scopesData = response?.data?.data ?? [];
      store.dispatch(setScopesOfSTOperation(scopesData)); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      })
    );
    return status;
  }
}

// get campaigns without scope
async function getCampaignsWithoutScope(
  oauthTokenHeader: string,
  queryId: string,
  website_shared_uid: string,
  account_shared_uid: string
) {
  try {
    const response = await stOperationService.getCampaignsWithoutScope(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      account_shared_uid
    );
    if (!response || !response.status) {
      throw new Error("Failed to access campaigns without scope.");
    } else if (response.status === 200) {
      const campaignsWithoutScope = response?.data?.data?.query_output ?? [];
      const filteredCampaigns = campaignsWithoutScope?.filter(
        (c: any) => c.cul_camp_status === "active"
      ); // only active unassigned campaigns
      store.dispatch(setCampaignsWithoutScopeData(filteredCampaigns)); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      })
    );
    return status;
  }
}

// get negative definition in both list and adgroup
async function getNegativeDefinitionBothListAndAdgroup(
  oauthTokenHeader: string,
  queryId: string,
  website_shared_uid: string,
  account_shared_uid: string
) {
  try {
    const response =
      await stOperationService.getNegativeDefinitionBothListAndAdgroup(
        oauthTokenHeader,
        queryId,
        website_shared_uid,
        account_shared_uid
      );
    if (!response || !response.status) {
      throw new Error(
        "Failed to access negative definition in both list and adgroup."
      );
    } else if (response.status === 200) {
      const negativesInListAndInAdGroup =
        response?.data?.data?.query_output ?? [];
      store.dispatch(
        setNegativesInListAndInAdGroupData(negativesInListAndInAdGroup)
      ); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      })
    );
    return status;
  }
}

// get negative definition in the campaign
async function getNegativeDefinitionInCampaign(
  oauthTokenHeader: string,
  queryId: string,
  website_shared_uid: string,
  account_shared_uid: string
) {
  try {
    const response = await stOperationService.getNegativeDefinitionInCampaign(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      account_shared_uid
    );
    if (!response || !response.status) {
      throw new Error("Failed to access negative definition in the campaign.");
    } else if (response.status === 200) {
      const negativesInTheCampaign = response?.data?.data?.query_output ?? [];
      store.dispatch(setNegativesInTheCampaignData(negativesInTheCampaign)); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      })
    );
    return status;
  }
}

// create new scope
async function createNewScope(oauthTokenHeader: string, data: any) {
  try {
    const response = await stOperationService.createNewScope(
      oauthTokenHeader,
      data
    );
    // Ensure response exists and check for both 200 and 201
    if (!response || !response.status) {
      return { reason: "Failed to create new scope." };
    }

    if (response.status === 200) {
      return response?.data;
    } else if (response.status === 201) {
      return response?.data;
    } else {
      return { reason: `Unexpected status code: ${response.status}` };
    }
  } catch (e: any) {
    return e?.parsedText || { reason: "Unknown error occurred." };
  }
}

// update existing scope name
async function updateExistingScope(
  oauthTokenHeader: string,
  data: any,
  scope_shared_uid: any
) {
  try {
    const response = await stOperationService.editScope(
      oauthTokenHeader,
      data,
      scope_shared_uid
    );
    // Ensure response exists and check for both 200 and 201
    if (!response || !response.status) {
      return { reason: "Failed to update scope." };
    }

    if (response.status === 200) {
      return response?.data;
    } else if (response.status === 201) {
      return response?.data;
    } else {
      return { reason: `Unexpected status code: ${response.status}` };
    }
  } catch (e: any) {
    return e?.parsedText || { reason: "Unknown error occurred." };
  }
}

// link between campaign to scope
async function linkBetweenCampaignToScope(oauthTokenHeader: string, data: any) {
  try {
    const response = await stOperationService.linkBetweenCampaignToScope(
      oauthTokenHeader,
      data
    );
    // Ensure response exists and check for both 200 and 201
    if (!response || !response.status) {
      // return { reason: "Failed to link between campaign and scope." };
      return {
        success: false,
        status: null,
        reason: "Failed to link between campaign and scope.",
      };
    }

    if (response.status === 201) {
      //return response?.data;
      // All records created successfully
      return {
        success: true,
        status: 201,
        data: response.data,
      };
    } else if (response.status === 207) {
      //return response?.data;
      // Multi-status: some succeeded, some failed
      const responseData = response.data;
      const hasSuccesses = responseData?.data?.some(
        (item: any) => item.created === true || item.updated === true
      );
      
      const hasFailures = responseData?.data?.some(
        (item: any) => item.created === false && item.updated === false
      );

      return {
        success: hasSuccesses, // true if at least one succeeded
        status: 207,
        partialSuccess: hasSuccesses && hasFailures,
        allFailed: !hasSuccesses && hasFailures,
        data: responseData,
        message: responseData?.reason || "Multi-status response",
      };
    } else if (response.status === 400) {
      return {
        success: false,
        status: 400,
        reason: "Failed to link between campaign and scope.",
      };
    } else {
      return {
        success: false,
        status: response.status,
        reason: `Unexpected status code: ${response.status}`,
      };
    }
  } catch (e: any) {
    //return e?.parsedText || { reason: "Unknown error occurred." };
    return {
      success: false,
      status: e?.status || null,
      reason: e?.parsedText?.reason || e?.message || "Unknown error occurred.",
      error: e,
    };
  }
}

async function unlinkCampaigsFromCurrentScope(
  oauthTokenHeader: string,
  data: any
) {
  try {
    const response = await stOperationService.unlinkCampaigsFromScope(
      oauthTokenHeader,
      data
    );
    
    // Ensure response exists and has a status
    if (!response || !response.status) {
      return { reason: "Failed to unlink campaigns from scope." };
    }

    // 204: All campaigns unlinked successfully
    if (response.status === 204) {
      return { reason: "success" };
    } 
    // 207: Partial success - some succeeded, some failed
    else if (response.status === 207) {
      const responseData = response.data;
      const dataArray = responseData?.data || [];
      
      // Separate successful and failed unlinking operations
      const successfulUnlinks = dataArray.filter((item: any) => item.unlink === true);
      const failedUnlinks = dataArray.filter((item: any) => item.unlink === false);
      
      return {
        reason: responseData?.reason || "Some records failed to unlink.",
        totalCount: dataArray.length,
        successCount: successfulUnlinks.length,
        failureCount: failedUnlinks.length,
        successfulUnlinks,
        failedUnlinks,
        partialSuccess: true
      };
    } 
    // Unexpected status code
    else {
      return { reason: `Unexpected status code: ${response.status}` };
    }
  } catch (e: any) {
    return e?.parsedText || { reason: "Unknown error occurred." };
  }
}

// remove negative terms
async function removeNegativeTerms(oauthTokenHeader: string, reqBody: any) {
  try {
    const response = await stOperationService.removeNegativeTerms(
      oauthTokenHeader,
      reqBody
    );

    if (!response || !response.status) {
      throw new Error(
        "Failed to remove negative terms. Please try again later."
      );
    } else if (response.status === 200) {
      return response.data;
    }
  } catch (e: any) {
    const message = { reason: "Failed to remove negative terms." }; // custom message for error handling
    return e ? e?.parsedText : message;
  }
}

export const STOperationEndPointLogic = {
  getAccounts,
  getScopes,
  getCampaignsWithoutScope,
  getNegativeDefinitionBothListAndAdgroup,
  getNegativeDefinitionInCampaign,
  createNewScope,
  linkBetweenCampaignToScope,
  removeNegativeTerms,
  updateExistingScope,
  unlinkCampaigsFromCurrentScope,
};
